import React from 'react';
import { Button } from 'antd';
import { KeyRound, Sparkles } from 'lucide-react';
import { useApiKeyManager } from './ApiKeyManagerProvider';
import './ApiKeyCard.css';

/**
 * ApiKeyCard — compact embedded view of the user's API keys.
 *
 * Used anywhere a delegation/profile page needs a "connect / manage your keys"
 * surface without taking over the whole viewport. Reads from the shared
 * ApiKeyManagerProvider, so no duplicate fetching. Clicking the primary action
 * opens the full-screen ApiKeyManager modal.
 *
 * Renders differently based on key count:
 *   0 keys  → empty state + "Get API Key" primary CTA
 *   N keys  → latest-used key summary + "Manage API Keys" ghost CTA
 */

const formatWhen = (iso) => {
  if (!iso) return 'Never used';
  const now = new Date();
  const then = new Date(iso);
  const dMin = Math.floor((now - then) / 60000);
  if (dMin < 1) return 'Just now';
  if (dMin < 60) return `${dMin}m ago`;
  const dHr = Math.floor(dMin / 60);
  if (dHr < 24) return `${dHr}h ago`;
  const dDay = Math.floor(dHr / 24);
  if (dDay < 30) return `${dDay}d ago`;
  return then.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const ApiKeyCard = () => {
  const { keys, activeCount, loading, hasLoadedOnce, open } = useApiKeyManager();

  // Loading state: show skeleton-like card, avoid flashes of "no keys" before
  // the initial fetch lands
  if (loading && !hasLoadedOnce) {
    return (
      <div className="api-key-card api-key-card-loading">
        <div className="api-key-card-header">
          <div className="api-key-card-title">API Keys</div>
        </div>
      </div>
    );
  }

  // Empty state
  if (activeCount === 0) {
    return (
      <div className="api-key-card">
        <div className="api-key-card-empty">
          <div className="api-key-card-empty-icon">
            <KeyRound size={18} />
          </div>
          <div className="api-key-card-empty-body">
            <div className="api-key-card-empty-title">No keys yet</div>
            <div className="api-key-card-empty-subtitle">
              Generate a key to connect your agents.
            </div>
          </div>
        </div>
        <Button
          type="primary"
          size="large"
          icon={<Sparkles size={16} />}
          onClick={open}
        >
          Get API Key
        </Button>
      </div>
    );
  }

  // Populated state — highlight the most recently used (or most recently
  // created if nothing's been used yet)
  const mostRecent = [...keys].sort((a, b) => {
    const aTime = new Date(a.last_used_at || a.created_at).getTime();
    const bTime = new Date(b.last_used_at || b.created_at).getTime();
    return bTime - aTime;
  })[0];

  const remaining = activeCount - 1;

  return (
    <div className="api-key-card">
      <div className="api-key-card-summary">
        <div className="api-key-card-summary-row">
          <div className="api-key-card-summary-icon">
            <KeyRound size={16} />
          </div>
          <div className="api-key-card-summary-main">
            <div className="api-key-card-summary-title">
              {mostRecent.name}
            </div>
            <div className="api-key-card-summary-meta">
              <code className="api-key-card-summary-prefix">{mostRecent.key_prefix}</code>
              <span className="api-key-card-summary-sep">·</span>
              <span>last used {formatWhen(mostRecent.last_used_at)}</span>
            </div>
          </div>
        </div>
        {remaining > 0 && (
          <div className="api-key-card-summary-others">
            + {remaining} more {remaining === 1 ? 'key' : 'keys'}
          </div>
        )}
      </div>
      <Button onClick={open}>
        Manage API Keys
      </Button>
    </div>
  );
};

export default ApiKeyCard;
